import { Api } from '../api/root-api';
import {
  SendOTPRequest,
  SendOTPResponse,
  VerifyOTPRequest,
  User,
  FCMTokenRequest,
  FCMTokenResponse,
  loginOnBehalfRequest,
  UserTokens,
  UserSubscriptionResponse,
  UserSubscriptionRequest,
  UserPermissionsResponse,
  SubscriptionRequest,
  SubscriptionResponse,
  ResendOTPRequest,
  ResendOTPResponse,
  AddMFAChannelRequest,
  AddMFAChannelResponse,
  WorkspaceAutRequest,
  WorkspaceAutResponse,
  TrackEventRequest,
  TrackEventType,
} from './model';
import {
  updateOtpValue,
  updateRefreshToken,
  updateToken,
  updateUserRoles,
  updateInviteUserType,
  storeSupportUserTokens,
  updateUser,
  updateLogInTracked,
  onSendOTPSuccess,
  onResendOTPSuccess,
  onAuthSuccess,
  onRefreshToken,
  updateUserPermissions,
  updateMappingDataForSupportUser,
} from './slice';

export const userApi = Api.injectEndpoints({
  endpoints: (build) => ({
    sendOTP: build.mutation<SendOTPResponse, SendOTPRequest>({
      query: (SendOTPRequest) => {
        return {
          url: 'sendotp',
          body: SendOTPRequest,
          method: 'POST',
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled.then((response) => {
          dispatch(onSendOTPSuccess(response.data));
        });
      },
    }),
    resendOTP: build.mutation<ResendOTPResponse, ResendOTPRequest>({
      query: (ResendOTPRequest) => {
        return {
          url: 'resend-otp',
          body: ResendOTPRequest,
          method: 'POST',
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled.then((response) => {
          dispatch(onResendOTPSuccess(response.data));
        });
      },
    }),
    getMappedWorkspacesForSupportUser: build.mutation<
      any,
      {
        supportUserId: string;
      }
    >({
      query(arg) {
        return {
          url: `admin/user/${arg.supportUserId}`,
          method: 'GET',
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled.then((response) => {
          dispatch(updateMappingDataForSupportUser(response.data));
        });
      },
    }),
    getSupportUserWorkSpaceRole: build.query<any, any>({
      query(args) {
        return {
          url: `admin/user/workspace-roles`,
          method: 'GET',
          params: args,
        };
      },
    }),
    verifyOTP: build.mutation<User, VerifyOTPRequest>({
      query: (args) => {
        return {
          url: 'verifyotp',
          body: args,
          method: 'POST',
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        dispatch(updateOtpValue(request));
        queryFulfilled.then((response) => {
          dispatch(onAuthSuccess(response.data));
        });
      },
    }),
    addMfaChannel: build.mutation<AddMFAChannelResponse, AddMFAChannelRequest>({
      query(arg) {
        return {
          url: 'change/sendOtp',
          method: 'POST',
          body: arg,
        };
      },
    }),
    verifyOTPPrincipalInvite: build.mutation<User, VerifyOTPRequest>({
      query: (args) => {
        return {
          url: 'verifyotp',
          body: args,
          method: 'POST',
        };
      },
      async onQueryStarted(request, { dispatch }) {
        dispatch(updateOtpValue(request));
      },
    }),
    getUser: build.query<User, any>({
      query: (args) => {
        return {
          url: 'users/me',
          method: 'GET',
          params: args,
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled.then((response) => {
          dispatch(updateUser(response.data));
        });
      },
    }),
    getUserV2: build.query<User, any>({
      query: (args) => {
        return {
          url: 'users/me/v2',
          method: 'GET',
          params: args,
        };
      },
      providesTags: ['userData'],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled.then((response) => {
          dispatch(updateUser(response.data));
        });
      },
    }),
    getUserPermissions: build.query<UserPermissionsResponse[], void>({
      query: () => {
        return {
          url: 'workspaces/roles/details',
          method: 'GET',
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        dispatch(
          userApi.endpoints.trackEvent.initiate({
            eventType: TrackEventType.LOG_IN,
          })
        );
        queryFulfilled.then((response) => {
          dispatch(updateUserPermissions(response.data));
        });
      },
    }),
    updateFCMToken: build.mutation<FCMTokenResponse, FCMTokenRequest>({
      query: (FCMTokenRequest) => {
        return {
          url: `/hub/communication-v2/api/fcm`,
          body: FCMTokenRequest,
          method: 'POST',
        };
      },
    }),
    loginOnBehalf: build.mutation<User, loginOnBehalfRequest>({
      query: (loginOnBehalfRequest) => {
        return {
          url: 'user/generateTokenonBehalf',
          body: loginOnBehalfRequest,
          method: 'POST',
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled.then((response) => {
          dispatch(storeSupportUserTokens(request.workspaceId));
          dispatch(updateToken(response.data.token));
          dispatch(updateRefreshToken(response.data.refreshToken));
          dispatch(updateInviteUserType(response.data.inviteUserType));
        });
      },
    }),
    updateUser: build.mutation<any, any>({
      query(arg) {
        return {
          url: 'users',
          method: 'PUT',
          body: arg,
        };
      },
      invalidatesTags: ['userData'],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((response) => {
            console.log(response);
            dispatch(
              updateUser({
                firstName: response?.data?.data?.firstName,
                lastName: response?.data?.data?.lastName,
                mobile: response?.data?.data?.mobile,
                city: response?.data?.data?.city,
                role: response?.data?.data?.role,
                profilePic: response?.data?.data?.profilePic,
                email: response?.data?.data?.email,
                address: response?.data?.data?.address,
                isVerified: response?.data?.data?.isVerified,
                isProfileUpdate: response?.data?.data?.isProfileUpdate,
                subtitle: response?.data?.data?.subtitle,
                userType: response?.data?.data?.userType,
                contentType: response?.data?.data?.contentType,
                isEmailVerified: response?.data?.data?.isEmailVerified,
              })
            );
          })
          .catch((x) => {
            console.error(x);
          });
      },
    }),
    regenerateToken: build.mutation<
      UserTokens,
      { refreshToken: string; token: string; workspaceId?: string }
    >({
      query(arg) {
        return {
          url: 'regenerate/token',
          body: arg,
          method: 'POST',
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((response) => {
            dispatch(onRefreshToken(response.data));
            if (response?.data?.workspaceRoles)
              dispatch(updateUserRoles(response?.data?.workspaceRoles));
          })
          .catch((x) => {
            console.error(x);
          });
      },
    }),
    userSubscription: build.query<
      UserSubscriptionResponse[],
      UserSubscriptionRequest
    >({
      query(arg) {
        return {
          url: `app/list/${arg.workspaceId}`,
          method: 'GET',
        };
      },
    }),
    subscribeApp: build.mutation<SubscriptionResponse, SubscriptionRequest>({
      query(arg) {
        return {
          url: `apps/subscribe/${arg.workspaceId}`,
          method: 'POST',
          body: arg,
        };
      },
    }),

    logout: build.mutation<string, void>({
      query() {
        return {
          url: 'logout',
          method: 'PUT',
          responseHandler(response) {
            return response.text();
          },
        };
      },
      async onQueryStarted(_, { dispatch }) {
        dispatch(
          userApi.endpoints.trackEvent.initiate({
            eventType: TrackEventType.LOG_OUT,
          })
        );
      },
    }),

    getWorkspaceAuthDetails: build.query<
      WorkspaceAutResponse,
      WorkspaceAutRequest
    >({
      query(arg) {
        return {
          url: `admin/workspaceAuthentication/details`,
          method: 'GET',
          params: arg,
        };
      },
    }),

    generateWorkspaceAuth: build.query<any, WorkspaceAutRequest>({
      query(arg) {
        return {
          url: `admin/authkey/generate`,
          method: 'GET',
          params: arg,
        };
      },
    }),
    trackEvent: build.mutation<void, TrackEventRequest>({
      query(arg) {
        return {
          url: `track`,
          method: 'POST',
          body: arg,
          keepalive: true,
        };
      },
      async onQueryStarted(request, { dispatch }) {
        if (request.eventType === TrackEventType.LOG_IN) {
          dispatch(updateLogInTracked(true));
        }
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useSendOTPMutation,
  useResendOTPMutation,
  useVerifyOTPMutation,
  useAddMfaChannelMutation,
  useVerifyOTPPrincipalInviteMutation,
  useUpdateFCMTokenMutation,
  useLazyGetUserQuery,
  useLazyGetUserV2Query,
  useGetUserQuery,
  useLoginOnBehalfMutation,
  useUpdateUserMutation,
  useRegenerateTokenMutation,
  useLogoutMutation,
  useLazyUserSubscriptionQuery,
  useLazyGetUserPermissionsQuery,
  useSubscribeAppMutation,
  useGetMappedWorkspacesForSupportUserMutation,
  useLazyGetWorkspaceAuthDetailsQuery,
  useLazyGenerateWorkspaceAuthQuery,
  useLazyGetSupportUserWorkSpaceRoleQuery,
  useTrackEventMutation,
} = userApi;
